/**
 * Clear this browser's locally-held engagement data.
 *
 * Why this exists:
 *   Signing out used to call `instance.logoutRedirect()` and nothing else.
 *   MSAL keeps its tokens in sessionStorage (see src/msalConfig.ts), so those
 *   did go — but every piece of client content lives in localStorage and
 *   IndexedDB, and none of it is namespaced by the signed-in user. On a shared
 *   laptop or a pooled VDI session the next person to sign in landed on /home
 *   and saw the previous consultant's client projects, could open them, and
 *   the page rehydrated their IRL, agenda and folder state from local storage
 *   without ever consulting the server.
 *
 * What this is NOT:
 *   A substitute for namespacing storage by user. This only runs when someone
 *   signs out deliberately — close the tab, crash the browser, or walk away
 *   and the data is still there. Per-user key namespacing is the durable fix
 *   and is tracked separately.
 *
 * Keys are enumerated deliberately rather than calling localStorage.clear(),
 * so nothing belonging to another app on the same origin is destroyed.
 */

/** Exact keys the app owns. Sourced by grepping every storage key constant in
 *  src/; keep in step when a new one is introduced. */
const EXACT_KEYS = [
  "projects_v1",
  "__irlRequests",
  "__irlMeta",
  "__irlAnswers",
  "__irlMatches",
  "__irlFolderRefs",
  "__irlMatchExcludedFolders",
  "__irlMatchPanelOpen",
  "__irlMatchSelectionInitialized",
  "__agendaInputs",
  "__agendaDatabookPin",
  "__pptFindingsContacts",
  "__returnToRoute",
];

/** Keys built per project or per IRL, so the exact name is not known up front.
 *  These carry the heaviest client content — the VDR index rows in particular
 *  are a full listing of a data room's contents. */
const KEY_PREFIXES = [
  "__databookFyEnd:", // src/fdd/tools/databook/DatabookPanel.tsx
  "__irlIndexMap_", // src/store/folderStore.ts
  "__irlIndexRows_", // "
  "__irlIndexRowsTs_", // "
  "__irlProvider_", // "
  "__irlSyncTs_", // "
  "__irlExcludedFolders_", // " (folder-mirroring exclusions)
];

/** sessionStorage keys used by the OneDrive redirect picker. */
const SESSION_KEYS = [
  "__returnToRoute",
  "onedrive_options",
  "onedrive_result",
  "onedrive_cancelled",
  "onedrive_error",
];

/** IndexedDB databases the app creates. */
const IDB_DATABASES = ["irl-agenda"];

/**
 * Remove all locally-stored engagement data. Never throws — sign-out must not
 * be blocked by a storage failure, and a partial clear beats none.
 *
 * IndexedDB deletion is fire-and-forget: `deleteDatabase` blocks indefinitely
 * while another tab holds the database open, and waiting on that would hang
 * the sign-out.
 */
export function clearEngagementState(): void {
  try {
    for (const key of EXACT_KEYS) localStorage.removeItem(key);

    // Prefixed keys have to be discovered — snapshot first, because removing
    // while iterating localStorage shifts the indices.
    const prefixed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && KEY_PREFIXES.some((p) => k.startsWith(p))) prefixed.push(k);
    }
    for (const k of prefixed) localStorage.removeItem(k);
  } catch {
    /* storage disabled or full — nothing useful to do here */
  }

  try {
    for (const key of SESSION_KEYS) sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }

  try {
    for (const name of IDB_DATABASES) indexedDB.deleteDatabase(name);
  } catch {
    /* ignore */
  }
}

/** Copy shown before clearing. Signing out discards local work that was never
 *  sent to the server, so the user gets to decide. */
export const CLEAR_ON_SIGNOUT_PROMPT =
  "Sign out and clear this computer's local project data?\n\n" +
  "Anything not yet saved to the server — IRL edits, agenda notes, folder " +
  "selections — will be removed from this browser. Saved databooks are " +
  "unaffected.";
